import { Checkbox, CircularProgress, Menu, Skeleton, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Tooltip } from "@mui/material";
import { Box } from "@mui/system";
import Cubes from "components/Icons/Cubes";
import useCatchApiError from "hooks/useCatchApiError";
import useFlowRoles from "hooks/useFlowRoles";
import { useSnackbarCustom } from "hooks/useSnackCustom";
import { IAbstractModel } from "model/abstract.model";
import DfeService from "pages/dfe/dfe.service";
import EventoNotaFiscal, { StateEventoNotaDialog } from "pages/evento-nota-fiscal/EventoNotaFiscal";
import { FlowEventoNotaEmitidaTypeDTO, possuiRejeicao } from "pages/evento-nota-fiscal/evento-nota-fiscal.model";
import { useCallback, useRef, useState } from "react";
import { useAppSelector } from "redux/hooks";

import { convertRelatorioDate, currencyOf } from "utils/util";
import Ellipsis from "./Ellipsis";
import CancelaCoisasDialog from "./Header/CancelaCoisasDialog";
import CustomLoading from "./Header/CustomLoading";
import EditaVendedor from "./Header/EditaVendedor";
import MenuItemAberto from "./Header/MenuItemAberto";
import MenuItemAgrupado from "./Header/MenuItemAgrupado";
import MenuItemCancelado from "./Header/MenuItemCancelado";
import MenuItemFaturado from "./Header/MenuItemFaturado";
import TableCellWithTooltip from "./Header/TableCellWithTooltip";
import CancelaDFeDialog from "./dialogs/CancelaDFeDialog";
import VendaService from "./venda.service";
import { Assosicacao, ColumnsRelatorioVendas, FlowEditaVendedorTO, ItemRelatorioVendas, StatusNotaFiscal, StatusPedido, TVendasTableProps, getAssociacao } from "./vendas.model";
import { customColors, theme } from "theme";

const relatorioColumnsLabels: ColumnsRelatorioVendas = [
    { sizePercent: '1', key: 'checkbox', label: '' },
    { sizePercent: '1', key: 'iconAgrupar', label: '' },
    { sizePercent: '5', key: 'numeroPedido', label: 'Pedido', },
    { sizePercent: '11', key: 'cliente', label: 'Cliente', },
    { sizePercent: '11', key: 'vendedor', label: 'Vendedor', },
    { sizePercent: '8', key: 'emissao', label: 'Emissão', type: 'date' },
    { sizePercent: '7', key: 'valorPedido', label: 'Total do pedido', type: 'currency' },
    { sizePercent: '4', key: 'serie', label: 'Serie', },
    { sizePercent: '3', key: 'numeroNotaFiscal', label: 'Nota', },
    { sizePercent: '7', key: 'valorNota', label: 'Total da nota', type: 'currency' },
    { sizePercent: '5', key: 'modelo', label: 'Modelo', },
    { sizePercent: '7', key: 'statusPedido', label: 'Status pedido' },
    {
        sizePercent: '5',
        key: 'valorItensTroca',
        label: '',
        type: 'icon',
        iconOpt: {
            className: 'fa-solid fa-retweet',
            color: 'green',
            sizePx: 18,
        },
        tooltipOpt: {
            content: '',
            position: 'right'
        }
    },
    { sizePercent: '2', key: 'acao', label: '', },
];

const initialState = {
    showCancelarModal: false,
    showCancelarDFeDialog: false,
    loading: false,
    showEditarVendedor: false,
    isChangingVendedor: false
};

const initialStateEventoNotaDialog: StateEventoNotaDialog = {
    open: false,
    info: {
        eventoType: FlowEventoNotaEmitidaTypeDTO.SEM_EVENTO,
    }
};

const VendasTable = (props: TVendasTableProps) => {
    const [stateLocal, setStateLocal] = useState(initialState);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openMenuRow = Boolean(anchorEl);
    const selectedItemref = useRef<ItemRelatorioVendas | null>(null);
    const vendasService = VendaService();
    const dfeService = DfeService();
    const { addSuccess, addError } = useSnackbarCustom();
    const estabelecimento = useAppSelector(e => e.estabelecimentoSelecionado);
    const [stateEventoNotaDialog, setStateEventoNotaDialog] = useState(initialStateEventoNotaDialog);
    const { throwErrorMessage } = useCatchApiError();
    const { venda, preVenda } = useFlowRoles();
    const vendaAcoes = {
        ...venda.acoes,
        canNavegaPreVenda: preVenda.canNovoPrevenda,
        canNavegaPDV: venda.canAcessarPDV,
    };

    const onClickEllipsis = (event: React.MouseEvent<HTMLButtonElement>, item: ItemRelatorioVendas) => {
        if (item.statusPedido === StatusPedido.ABERTO.toUpperCase()
            || item.statusPedido === StatusPedido.FATURADO.toUpperCase()
            || item.statusPedido === StatusPedido.AGRUPADO.toUpperCase()
        ) { // descomentar o if em futuras versões (Atual 2.30.x)
            selectedItemref.current = item;
            setAnchorEl(event.currentTarget);
        }
    };

    const onCloseMenuRow = () => {
        setAnchorEl(null);
        setTimeout(() => {
            selectedItemref.current = null;
        }, 100);
    };

    const onOpen = (key: keyof typeof initialState) => {
        setStateLocal(prevState => ({ ...prevState, [key]: true }));
    };

    const imprimirDFE = useCallback((uuid: string) => {
        setStateLocal(prevState => ({ ...prevState, loading: true }));
        onCloseMenuRow();
        vendasService.getUrlDanfe(uuid)
            .then(({ data }: { data: string; }) => {
                setStateLocal(prevState => ({ ...prevState, loading: false }));
                props.onShowImprimirDFE!(data);
            })
            .catch((err) => {
                throwErrorMessage(err);
                setStateLocal(prevState => ({ ...prevState, loading: false }));
            });
        // eslint-disable-next-line
    }, [estabelecimento.uuid]);

    const excluirNota = useCallback((chaveAcesso: string) => {
        setStateLocal(prevState => ({ ...prevState, loading: true }));
        onCloseMenuRow();
        vendasService.excluirNota(chaveAcesso)
            .then(() => {
                setStateLocal(prevState => ({ ...prevState, loading: false }));
                addSuccess({ message: 'Nota excluída com sucesso', closeable: true });
            })
            .catch(() => {
                addError({ message: 'Não foi possível excluir a nota', closeable: true });
            })
            .finally(() => {
                props.onAtualizarRelatorio();
                setStateLocal(prevState => ({ ...prevState, loading: false }));
            });
        // eslint-disable-next-line
    }, []);

    const onClose = useCallback((key: keyof typeof initialState) => {
        onCloseMenuRow();
        setStateLocal(prevState => ({ ...prevState, [key]: false }));
    }, []);

    const getCor = useCallback((status: string) => {
        const key = Object.keys(Assosicacao).find(e => getAssociacao(e).statusNotaFiscal === status);
        return getAssociacao(key ?? "")?.rgb;
    }, []);

    const isValidButton = useCallback((item: ItemRelatorioVendas) => {
        // Atualizar ao mexer nas ações do relatorio, até um ponto que não haverá
        // necessidade dessa função, pois todos statusNotaFiscal terão suas devidas ações

        if (item.statusPedido === StatusPedido.FATURADO.toUpperCase()) {
            return item.statusNotaFiscal !== StatusNotaFiscal.CANCELADA;
        }
        if (item.statusPedido === StatusPedido.ABERTO.toUpperCase()) {
            return true;
        }
        if (item.statusPedido === StatusPedido.CANCELADO.toUpperCase()) {
            return false;
        }
        if (item.statusPedido === StatusPedido.AGRUPADO.toUpperCase()) {
            return true;
        }
        return false;
    }, []);

    const imprimirPedidoA4 = useCallback((uuid: string) => {
        onCloseMenuRow();
        props.onShowRomaneioVenda!(uuid);
        //eslint-disable-next-line
    }, [estabelecimento.uuid]);

    const imprimirEtiquetaVolume = useCallback((uuid: string) => {
        onCloseMenuRow();
        props.onImprimirEtiquetaVenda(uuid);
        //eslint-disable-next-line
    }, [estabelecimento.uuid]);

    const onImprimirEtiquetaSimplificada = useCallback((chaveAcesso: string) => {
        onCloseMenuRow();
        props.onImprimirEtiquetaSimplificada(chaveAcesso);
        //eslint-disable-next-line
    }, [estabelecimento.uuid]);

    const imprimirCrediario = useCallback((uuid: string, emissaoNota: Date | undefined) => {
        onCloseMenuRow();
        props.onImprimirCrediario!(uuid, emissaoNota);
        //eslint-disable-next-line
    }, [estabelecimento.uuid]);

    const onImprimirPreVenda = useCallback((itemRelatorio: ItemRelatorioVendas) => {
        onCloseMenuRow();
        props.onImprimirPreVenda!(itemRelatorio);
        //eslint-disable-next-line
    }, [estabelecimento.uuid]);

    const imprimirPedido = useCallback((uuid: string) => {
        onCloseMenuRow();
        props.onShowImprimirOutros!(uuid);
        // eslint-disable-next-line
    }, [estabelecimento.uuid]);

    const editarVendedor = useCallback(() => {
        setStateLocal(prevState => ({ ...prevState, showEditarVendedor: true }));
        setAnchorEl(null);
        // eslint-disable-next-line
    }, [estabelecimento.uuid]);

    const navigatePdv = (uuid: string, numeroPedido: number) => {
        onCloseMenuRow();
        props.onNavegaPDV(uuid, numeroPedido);
    };

    const navigatePreVenda = (uuid: string, numeroPedido: number) => {
        onCloseMenuRow();
        props.onNavegaPreVenda!(uuid, numeroPedido);
    };

    const onEditaVendedor = useCallback((vendedor: IAbstractModel | null, outrosComissionados: Array<string>) => {
        if (!vendedor || !selectedItemref.current) return;
        setStateLocal(prevState => ({ ...prevState, isChangingVendedor: true }));

        const FlowEditaVendedorTO: FlowEditaVendedorTO = {
            pedidoUUID: selectedItemref.current.uuidPedido,
            vendedorUUID: vendedor.uuid,
            outrosComissionados: outrosComissionados
        };
        vendasService.editarVendedor(FlowEditaVendedorTO)
            .then(() => {
                addSuccess({
                    message: "Vendedor alterado com sucesso",
                    id: "alteracao_vendedor_sucesso",
                    persist: false,
                    preventDuplicate: true,
                    hideDuration: 2500
                });
                props.onAtualizarRelatorio();
                setStateLocal(prevState => ({ ...prevState, showEditarVendedor: false, isChangingVendedor: false }));
            })
            .catch((err) => {
                throwErrorMessage(err);
                setStateLocal(prevState => ({ ...prevState, showEditarVendedor: false, isChangingVendedor: false }));
            });
        // eslint-disable-next-line
    }, [selectedItemref.current]);

    const onVisualizar = useCallback((uuid: string, numeroPedido: number) => {
        onCloseMenuRow();
        props.onVisualizar(uuid, numeroPedido);
        //eslint-disable-next-line
    }, [estabelecimento.uuid]);

    const handleAtualizarSituacao = useCallback((chaveAcesso: string) => {
        setStateLocal(prevState => ({ ...prevState, loading: true }));
        onCloseMenuRow();
        dfeService.atualizarSituacao(chaveAcesso)
            .then(({ data }) => {
                setStateLocal(prevState => ({ ...prevState, loading: false }));
                if (possuiRejeicao(data.eventoType)) {
                    setStateEventoNotaDialog(prev => ({
                        open: true,
                        info: data,
                    }));
                } else {
                    addSuccess({ message: 'Documento fiscal atualizado com sucesso', closeable: true });
                }
            })
            .catch(() => {
                addError({ message: 'Não foi possível atualizar o documento fiscal', closeable: true });
            })
            .finally(() => {
                props.onAtualizarRelatorio();
                setStateLocal(prevState => ({ ...prevState, loading: false }));
            });
        // eslint-disable-next-line
    }, []);


    const downloadXML = (chaveAcesso: string) => {
        setStateLocal(prevState => ({ ...prevState, loading: true }));
        onCloseMenuRow();
        vendasService.baixarXML(chaveAcesso)
            .then(res => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${chaveAcesso}.xml`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                addSuccess({ message: 'Download realizado com sucesso', closeable: true });
            })
            .catch(() => {
                addError({ message: 'Não foi possível baixar o XML da nota', closeable: true });
            })
            .finally(() => {
                props.onAtualizarRelatorio();
                setStateLocal(prevState => ({ ...prevState, loading: false }));
            });
    };

    const handleNavigateConferencia = useCallback((uuid: string) => {
        onCloseMenuRow();
        props.handleNavegaConferencia(uuid);
        //eslint-disable-next-line
    }, []);

    const handleClick = useCallback((rowKey: string) => {
        props.setSelectedRows(prevSelected => {
            if (prevSelected.includes(rowKey)) {
                return prevSelected.filter(key => key !== rowKey);
            } else {
                return [...prevSelected, rowKey];
            }
        });
        //eslint-disable-next-line
    }, []);

    return (
        <>
            <TableContainer sx={{
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                position: 'relative',
                boxSizing: 'border-box',
                height: '500px',
                marginTop: '10px'
            }}
                key="vendasTable_table_container"
                className="color-orange">
                {props.isLoading &&
                    <Box sx={{ position: 'absolute', width: '100%', height: 'calc(100%)', bgcolor: 'rgba(0,0,0,0.05)', border: 0, padding: 0, zIndex: props.isLoading ? 100 : 1 }}>
                        <CircularProgress
                            size={36}
                            color='primary'
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                            }}
                        />
                    </Box>
                }
                <Table
                    id="content-table_vendas_table"
                    stickyHeader
                    size="small"
                    key="content-table_vendas_table"
                >
                    <TableHead key="header_table_vendas_table" id="header_table_vendas_table">
                        <TableRow key="header_row_table_vendas_table" id="header_row_table_vendas_table">
                            {relatorioColumnsLabels.map((header) => (
                                <TableCell
                                    sx={{ whiteSpace: 'nowrap', width: `${header.sizePercent}%`, textAlign: 'center', fontWeight: 'bold' }}
                                    key={`header_cell_table_${header.key}`}
                                    id={`header_cell_table_${header.key}`}
                                >
                                    {header.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody key="header_table_body">
                        {props.totalizadores.length === 0 && !props.totalizadoresError &&
                            Array.from([].constructor(7)).map((unknown, indexA) => (
                                <TableRow key={`body_table_${unknown}_${indexA}`}>
                                    {relatorioColumnsLabels.map((a, indexB) => {
                                        return <TableCell
                                            key={`cell_table_${a.key}_${indexB}`}
                                        >
                                            <Skeleton
                                                animation="wave"
                                                variant="rounded"
                                                width={'100%'}
                                                height={30}
                                            />
                                        </TableCell>;
                                    })}
                                </TableRow>
                            ))
                        }
                        {props.totalizadores.length === 0 && props.totalizadoresError &&
                            <TableRow sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                position: 'absolute',
                                top: '200px'
                            }}
                                key="sem_pedidos_table_row_error"
                            >
                                <TableCell
                                    key="row_sem_pedidos_table_cell_error"
                                    colSpan={12}
                                    sx={{
                                        fontSize: '48px',
                                        color: customColors.cinzaClaro,
                                        border: 0,
                                        padding: 0,
                                        userSelect: 'none'
                                    }}>Não há pedidos no período informado.</TableCell>
                            </TableRow>
                        }
                        {props.totalizadores.length > 0 && props.totalizadores.map((item, indexA) => {
                            return (
                                <TableRow
                                    key={item.numeroPedido.toString() + "-" + item.numeroNotaFiscal + indexA}
                                    style={{ backgroundColor: getCor(item.statusNotaFiscal) }}>
                                    {relatorioColumnsLabels.map((a, indexB) => {
                                        let valor: any = item[a.key];
                                        if (a.type === 'date') valor = convertRelatorioDate(valor.toLocaleString('pt-BR'));
                                        if (a.type === 'currency') valor = currencyOf(valor);
                                        return (
                                            <TableCell
                                                sx={{ textAlign: 'center', position: 'relative' }} id={`item_${indexB + 1}_row_${indexA}_${a.label}`}
                                                key={`tableCell_${a.key}_${indexB}`}
                                            >
                                                {a.key === 'checkbox' &&
                                                    <Tooltip title="AGRUPAR PEDIDO">
                                                        <Checkbox
                                                            onClick={() => handleClick(item.uuidPedido)}
                                                            sx={{
                                                                '&.MuiCheckbox-root.Mui-checked': {
                                                                    color: theme.palette.primary.main,
                                                                    border: 0,
                                                                    padding: 0
                                                                },
                                                                border: 0,
                                                                padding: 0
                                                            }}
                                                            checked={props.selectedRows.includes(item.uuidPedido)}
                                                            disabled={item.statusPedido !== "ABERTO"}
                                                        />
                                                    </Tooltip>
                                                }
                                                {a.key === "iconAgrupar" && item.pedidoAgrupamentoInfo && (
                                                    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{item.pedidoAgrupamentoInfo}</span>}>
                                                        <TableCell
                                                            sx={{
                                                                textOverflow: "ellipsis",
                                                                color: theme.palette.primary.main,
                                                                textAlign: 'center',
                                                                padding: 0,
                                                                border: 0,
                                                            }}
                                                        >
                                                            <Cubes />
                                                        </TableCell>
                                                    </Tooltip>

                                                )}
                                                {a.key === 'acao' &&
                                                    <Ellipsis
                                                        index={indexA}
                                                        onClick={onClickEllipsis}
                                                        item={item}
                                                        isValidButton={isValidButton}
                                                    />}

                                                {(a.key === "cliente" || a.key === 'vendedor') &&
                                                    <TableCellWithTooltip
                                                        content={valor ?? (a.key === 'cliente' ? 'CONSUMIDOR' : '')}
                                                        index={indexA}
                                                        tooltipPosition={indexA <= 4 ? 'bottom' : 'top'}
                                                    />}

                                                {a.type === "icon" && !!item.trocas?.length &&
                                                    <TableCellWithTooltip
                                                        content={item.trocas.map(s => `${s.serieTroca}/${s.numeroNotaFiscalTroca} - ${currencyOf(s.valorItensTroca)} - ${s.statusNotaFiscalTroca}`).join('  ')}
                                                        index={indexA}
                                                        isIcon
                                                        iconOpt={a.iconOpt}
                                                        tooltipPosition={indexA <= 4 ? 'bottom' : 'top'}
                                                    />}

                                                {(a.key !== 'vendedor' && a.key !== 'cliente' && a.type !== 'icon') &&
                                                    valor
                                                }
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}

                        <Menu key="vendas_table_menu"
                            anchorEl={anchorEl}
                            open={openMenuRow}
                            onClose={onCloseMenuRow}
                        >
                            {selectedItemref.current?.statusPedido === StatusPedido.FATURADO.toUpperCase() &&
                                <MenuItemFaturado
                                    itemRef={selectedItemref}
                                    onOpenCancelarDFe={() => onOpen('showCancelarDFeDialog')}
                                    onImprimirDFE={imprimirDFE}
                                    onImprimirPedido={imprimirPedido}
                                    onExcluirNota={excluirNota}
                                    onEditarVendedor={editarVendedor}
                                    onImprimirRomaneio={imprimirPedidoA4}
                                    onImprimirCrediario={imprimirCrediario}
                                    onDownloadXML={downloadXML}
                                    onVisualizar={onVisualizar}
                                    onAtualizarSituacao={handleAtualizarSituacao}
                                    onImprimirEtiquetaVolume={imprimirEtiquetaVolume}
                                    onImprimirEtiquetaSimplificada={onImprimirEtiquetaSimplificada}
                                    acoes={vendaAcoes}
                                />
                            }
                            {selectedItemref.current?.statusPedido === StatusPedido.ABERTO.toUpperCase() &&
                                <MenuItemAberto
                                    itemRef={selectedItemref}
                                    onCancelar={() => onOpen('showCancelarModal')}
                                    onNavigatePDV={navigatePdv}
                                    onEditarVendedor={editarVendedor}
                                    onImprimirPedido={imprimirPedido}
                                    onImprimirRomaneio={imprimirPedidoA4}
                                    onImprimirCrediario={imprimirCrediario}
                                    onImprimirPreVenda={onImprimirPreVenda}
                                    onNavigatePreVenda={navigatePreVenda}
                                    onVisualizar={onVisualizar}
                                    onNavigateConferencia={handleNavigateConferencia}
                                    onImprimirEtiquetaVolume={imprimirEtiquetaVolume}
                                    acoes={vendaAcoes}
                                />
                            }
                            {selectedItemref.current?.statusPedido === StatusPedido.CANCELADO.toUpperCase() &&
                                <MenuItemCancelado
                                    itemRef={selectedItemref}
                                    onVisualizar={onVisualizar}
                                    acoes={vendaAcoes}
                                />
                            }
                            {selectedItemref.current?.statusPedido === StatusPedido.AGRUPADO.toUpperCase() &&
                                <MenuItemAgrupado
                                    itemRef={selectedItemref}
                                    onVisualizar={onVisualizar}
                                    onImprimirRomaneio={imprimirPedidoA4}
                                    acoes={vendaAcoes}
                                />
                            }
                        </Menu>
                    </TableBody>

                    {!props.totalizadoresError &&
                        <TableFooter
                            sx={{
                                width: '100%',
                                position: props.totalizadores.length >= 11 ? 'sticky' : 'absolute',
                                bottom: 0,
                                height: '40px',
                            }} className='glassmorphism'>
                            <TableRow>
                                <TableCell id="vendas_footer_quantidade" sx={{ paddingLeft: '50px', fontWeight: 'bold', color: theme.palette.common.black, fontSize: '14px', border: 0 }} colSpan={5}>Quantidade: {props.dadosFooter.quantidade}</TableCell>
                                {props.totalizadores.length < 11 &&
                                    <TableCell id="vendas_footer_espaco_quantidade_absolute" sx={{ width: '50%', border: 0 }}></TableCell>
                                }
                                <TableCell id="vendas_footer_total_pedido" sx={{ paddingLeft: '20px', fontWeight: 'bold', color: theme.palette.common.black, fontSize: '14px', border: 0 }} colSpan={3}>{currencyOf(props.dadosFooter.totalPedido)}</TableCell>
                                {props.totalizadores.length < 11 &&
                                    <TableCell id="vendas_footer_espaco_total_pedido_absolute" sx={{ width: '19%', border: 0 }}></TableCell>
                                }
                                <TableCell id="vendas_footer_total_nota" sx={{ paddingLeft: '20px', fontWeight: 'bold', color: theme.palette.common.black, fontSize: '14px', border: 0 }}>{currencyOf(props.dadosFooter.totalNota)}</TableCell>
                            </TableRow>
                        </TableFooter>
                    }
                </Table>
            </TableContainer>
            <CancelaCoisasDialog
                header="Deseja cancelar o pedido?"
                open={stateLocal.showCancelarModal}
                onClose={() => { onClose('showCancelarModal'); }}
                item={selectedItemref.current}
                onConfirm={(item, uuidMotivo) => props.onCancelaPedido(item.uuidPedido, uuidMotivo)}
                columns={[
                    {
                        id: 'statusPedido',
                        header: 'Status',
                        key: 'statusPedido',
                        width: 'auto'
                    },
                    {
                        id: 'numeroPedido',
                        header: 'Pedido',
                        key: 'numeroPedido',
                        width: 'auto'
                    },
                    {
                        id: 'cliente',
                        header: 'Cliente',
                        key: 'cliente',
                        width: 'auto',
                    },
                    {
                        id: 'valorPedido',
                        header: 'Valor Total',
                        key: 'valorPedido',
                        width: 'auto',
                        convert: (item) => {
                            return <>{currencyOf(item.valorPedido)}</>;
                        },
                    },
                ]}
            />

            <CancelaDFeDialog
                open={stateLocal.showCancelarDFeDialog}
                onClose={() => {
                    props.onAtualizarRelatorio();
                    onClose('showCancelarDFeDialog');
                }}
                item={selectedItemref.current ? {
                    serie: selectedItemref.current.serie,
                    numeroNotaFiscal: selectedItemref.current.numeroNotaFiscal,
                    cliente: selectedItemref.current.cliente,
                    chaveAcesso: selectedItemref.current.chaveAcesso,
                    modelo: selectedItemref.current.modelo,
                    tipoNotaFiscal: 'VENDA'
                } : null}
            />

            {stateLocal.loading &&
                <CustomLoading />
            }
            {stateLocal.showEditarVendedor &&
                <EditaVendedor
                    itemRef={selectedItemref}
                    onClose={() => onClose("showEditarVendedor")}
                    onEditaVendedor={onEditaVendedor}
                />
            }
            {stateLocal.isChangingVendedor &&
                <CustomLoading />
            }

            <EventoNotaFiscal
                state={stateEventoNotaDialog}
                setState={setStateEventoNotaDialog}
                onClose={() => {
                    addSuccess({ message: 'Documento fiscal atualizado com sucesso', closeable: true });
                }}
            />
        </>
    );
};

export default VendasTable;
